import { CustomFilterWithGrid } from 'components/CustomTableFilter';
import { DateRangeFilter } from 'components/DateRangeFilter/DateRangeFilter';
import { useRequest } from 'features/request/useRequest';
import { useResetMultipleStoreData } from 'features/request/useResetStoreData';
import type { ProjectMethodologyEnum } from 'gql/graphql';
import { getMethodologyLabel } from 'types/methodology';

import { useEffect, useMemo } from 'react';

import {
    getCatalogsMethodologyFilterGql,
    getCatalogsNameFilterGql,
    getCatalogSubsidiaryFilterGql,
} from '../catalogListQueries';

export const useCatalogListFilters = () => {
    const memoizedFilterIds = useMemo(
        () => ['catalogsMethodologyFilter', 'catalogsNameFilter', 'catalogSubsidiaryFilter'],
        [],
    );

    const [resetCatalogListFilters] = useResetMultipleStoreData({
        requestIds: memoizedFilterIds,
    });

    useEffect(
        () => () => {
            resetCatalogListFilters();
        },
        [resetCatalogListFilters],
    );

    const [catalogsNameFilterData, , , catalogsNameFilterRequest] = useRequest({
        requestId: 'catalogsNameFilter',
        query: getCatalogsNameFilterGql,
        withEffect: false,
    });

    const [catalogsMethodologyFilterData, , , catalogsMethodologyFilterRequest] = useRequest({
        requestId: 'catalogsMethodologyFilter',
        query: getCatalogsMethodologyFilterGql,
        withEffect: false,
    });

    const [catalogSubsidiaryFilterData, , , catalogSubsidiaryFilterRequest] = useRequest({
        requestId: 'catalogSubsidiaryFilter',
        query: getCatalogSubsidiaryFilterGql,
        withEffect: false,
    });

    return useMemo(
        () => [
            {
                id: 'name',
                name: 'Название: ',
                field: 'name',
                filterer: () => true,
                component: {
                    name: CustomFilterWithGrid,
                    props: {
                        withSearch: true,
                        withSelectAllBtn: true,
                        items: catalogsNameFilterData?.catalogsNameFilter?.items,
                        requestFn: catalogsNameFilterRequest,
                        requestQuery: getCatalogsNameFilterGql,
                        dataAccessor: 'catalogsNameFilter',
                        requestVariables: null,
                        childFieldAccessor: 'value',
                        columnDefs: [
                            {
                                headerName: 'Название',
                                field: 'value',
                                lockVisible: true,
                                flex: 1,
                                checkboxSelection: true,
                            },
                        ],
                        whereVariables: ['value'],
                    },
                },
            },
            {
                id: 'methodology',
                name: 'Методология: ',
                field: 'type',
                filterer: () => true,
                component: {
                    name: CustomFilterWithGrid,
                    props: {
                        withSearch: false,
                        withSelectAllBtn: true,
                        items: catalogsMethodologyFilterData?.catalogsMethodologyFilter?.map(
                            ({ value }: { value: ProjectMethodologyEnum }) => ({
                                label: getMethodologyLabel(value),
                                value,
                            }),
                        ),
                        requestFn: catalogsMethodologyFilterRequest,
                        requestQuery: getCatalogsMethodologyFilterGql,
                        childFieldAccessor: 'label',
                        columnDefs: [
                            {
                                headerName: 'Методология',
                                field: 'label',
                                lockVisible: true,
                                flex: 1,
                                checkboxSelection: true,
                                resizable: true,
                            },
                        ],
                    },
                },
            },
            {
                id: 'updatedAt',
                name: 'Дата обновления: ',
                field: 'updatedAt',
                filterer: () => true,
                component: {
                    name: DateRangeFilter,
                },
            },
            {
                id: 'subsidiaryName',
                name: 'Дочернее общество: ',
                field: 'subsidiaryName',
                filterer: () => true,
                component: {
                    name: CustomFilterWithGrid,
                    props: {
                        withSearch: true,
                        withSelectAllBtn: true,
                        items: catalogSubsidiaryFilterData?.catalogSubsidiaryFilter?.items,
                        requestFn: catalogSubsidiaryFilterRequest,
                        requestQuery: getCatalogSubsidiaryFilterGql,
                        childFieldAccessor: 'value',
                        columnDefs: [
                            {
                                headerName: 'Дочернее общество',
                                field: 'value',
                                lockVisible: true,
                                flex: 1,
                                checkboxSelection: true,
                                resizable: true,
                            },
                        ],
                        whereVariables: ['value'],
                    },
                },
            },
        ],
        [
            catalogSubsidiaryFilterData?.catalogSubsidiaryFilter?.items,
            catalogSubsidiaryFilterRequest,
            catalogsMethodologyFilterData?.catalogsMethodologyFilter,
            catalogsMethodologyFilterRequest,
            catalogsNameFilterData?.catalogsNameFilter?.items,
            catalogsNameFilterRequest,
        ],
    ) as any;
};
